import { Colors } from "@/enums/Colors";
import { LazoCompany } from "@/models/LazoCompany";
import { LazoUser } from "@/models/LazoUser";
import LazoCompanyService from "@/services/LazoCompanyService";
import LazoUserService from "@/services/LazoUserService";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export type BasicWithName = {
  name: string;
  externalId: string;
};

export type Criteria = {
  startups: Partial<LazoCompany>[];
  lazoUsers: Partial<LazoUser>[];
  clientUsers: Partial<LazoUser>[];
  kams: Partial<LazoUser>[];
  status: BasicWithName[];
};

const THREADS_STATUS_OPTIONS: BasicWithName[] = [
  { name: "Open", externalId: "OPEN" },
  { name: "Answered", externalId: "ANSWERED" },
  { name: "Finalized", externalId: "FINALIZED" },
];

export type ToHandle = Partial<LazoCompany> | Partial<LazoUser> | BasicWithName;

interface Props {
  criteria: Criteria;
  handleChange: (field: string, selected: ToHandle[]) => void;
  clientUsers: Partial<LazoUser>[];
}

const filterConfig = [
  { field: "startups", label: "Startups" },
  { field: "status", label: "Status" },
  { field: "lazoUsers", label: "Lazo Users" },
  { field: "clientUsers", label: "Client Users" },
  { field: "kams", label: "KAMs" },
];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "50px",
    fontSize: "0.8125rem",
    backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: Colors.LIGHT_GRAY_DEDEE1,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: Colors.LAZO_PURPLE,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: Colors.LAZO_PURPLE,
      borderWidth: "1.5px",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
    color: Colors.LIGHT_GRAY_8A8E93,
    "&.Mui-focused": { color: Colors.LAZO_PURPLE },
  },
};

const PortalInboxStatisticsFilters = ({ criteria, handleChange, clientUsers }: Props) => {
  const [lazoUsers, setLazoUsers] = useState<Partial<LazoUser>[]>([]);
  const [kams, setKams] = useState<Partial<LazoUser>[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedInputValue(inputValue), 300);
    return () => clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    LazoUserService.getServiceTeamUsers().then((r) => {
      r.onSuccess(() => {
        const data = r.getContentOrThrowError();
        setLazoUsers(data.map(({ externalId, firstName, lastName }) => ({ externalId, name: `${firstName} ${lastName}` })));
      });
    });
    LazoUserService.getAllKam().then((r) => {
      r.onSuccess(() => {
        const data = r.getContentOrThrowError();
        setKams(data.map(({ user }) => ({ externalId: user.externalId, name: `${user.firstName} ${user.lastName}` })));
      });
    });
  }, []);

  const {
    data: startupOptions,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["startupsAutocomplete", debouncedInputValue],
    queryFn: () =>
      LazoCompanyService.getByTerm(debouncedInputValue).then((r) =>
        r.getContentOrThrowError().map((c) => ({ name: c.companyName, externalId: c.tenantExternalId }))
      ),
    enabled: debouncedInputValue.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.875rem" }}>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "0.625rem", flexWrap: "wrap" }}>
        <Autocomplete
          multiple
          size="small"
          options={[...criteria.startups, ...(startupOptions || [])]}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
          value={criteria.startups}
          onChange={(_e, value: Partial<LazoCompany>[]) => handleChange("startups", value)}
          renderTags={() => null}
          inputValue={inputValue}
          onInputChange={(_e, v) => setInputValue(v)}
          loading={isLoading || isFetching}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Startups"
              sx={{ width: "165px", ...inputSx }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading || isFetching ? <CircularProgress color="inherit" size={14} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          multiple
          size="small"
          options={THREADS_STATUS_OPTIONS}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
          value={criteria.status}
          renderTags={() => null}
          onChange={(_e, value: BasicWithName[]) => handleChange("status", value)}
          renderInput={(params) => <TextField {...params} label="Status" sx={{ width: "140px", ...inputSx }} />}
        />

        <Autocomplete
          multiple
          size="small"
          options={lazoUsers}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
          value={criteria.lazoUsers}
          renderTags={() => null}
          onChange={(_e, value: BasicWithName[]) => handleChange("lazoUsers", value)}
          renderInput={(params) => <TextField {...params} label="Lazo Users" sx={{ width: "150px", ...inputSx }} />}
        />

        <Autocomplete
          multiple
          size="small"
          options={clientUsers}
          renderTags={() => null}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
          value={criteria.clientUsers}
          onChange={(_e, value: BasicWithName[]) => handleChange("clientUsers", value)}
          renderInput={(params) => <TextField {...params} label="Client Users" sx={{ width: "150px", ...inputSx }} />}
        />

        <Autocomplete
          multiple
          size="small"
          options={kams}
          renderTags={() => null}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.externalId === value.externalId}
          value={criteria.kams}
          onChange={(_e, value: BasicWithName[]) => handleChange("kams", value)}
          renderInput={(params) => <TextField {...params} label="KAMs" sx={{ width: "140px", ...inputSx }} />}
        />
      </Box>

      {filterConfig.some(({ field }) => (criteria[field as keyof Criteria] as ToHandle[]).length > 0) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
          {filterConfig.map(({ field, label }) => {
            const items = criteria[field as keyof Criteria] as ToHandle[];
            if (!items.length) return null;
            return (
              <Box key={field} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.375rem" }}>
                <Typography
                  fontSize="0.6875rem"
                  fontWeight={600}
                  sx={{ color: Colors.LIGHT_GRAY_8A8E93, textTransform: "uppercase", letterSpacing: "0.06em", minWidth: "fit-content" }}
                >
                  {label}
                </Typography>
                {items.map((item) => (
                  <ActiveFilterChip
                    key={item.externalId}
                    label={item.name}
                    onDelete={() =>
                      handleChange(
                        field,
                        items.filter((i) => i.externalId !== item.externalId)
                      )
                    }
                    deleteIcon={<CloseIcon sx={{ fontSize: "0.875rem !important" }} />}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default PortalInboxStatisticsFilters;

const ActiveFilterChip = styled(Chip)(() => ({
  backgroundColor: Colors.LAZO_LIGHT_PURPLE,
  border: `1px solid ${Colors.LAZO_PURPLE2}`,
  borderRadius: "50px",
  height: "1.625rem",
  "& .MuiChip-label": {
    padding: "0 0.5rem",
    fontSize: "0.75rem",
    color: Colors.BLACK_151517,
  },
  "& .MuiChip-deleteIcon": {
    color: Colors.LAZO_PURPLE,
    marginRight: "0.375rem",
    "&:hover": { color: Colors.DARK_LAZO_PURPLE },
  },
}));
